import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Steps, Card, Button, Space, Form, Input, message, Spin, Result,
  Typography, Checkbox, Table, Alert, Radio, Tag, Tooltip, Divider, Empty,
} from 'antd';
import {
  ArrowLeftOutlined, ArrowRightOutlined, ReloadOutlined, CheckCircleFilled,
  ClockCircleOutlined, ThunderboltOutlined, CalendarOutlined,
} from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router';
import {
  getConnectorSpec, createConnection, updateConnection, deleteConnection,
  testConnection,
} from '../services/dataPlatformService';
import JsonSchemaForm from '../components/JsonSchemaForm';

const { Title, Paragraph, Text } = Typography;

const WIZARD_MAX_WIDTH = 960;
const WIZARD_STEP_SCHEDULE = 3;

function renderPreviewCell(v) {
  if (v == null) {return <Text type="secondary">—</Text>;}
  if (typeof v === 'object') {return <code style={{ fontSize: 11 }}>{JSON.stringify(v)}</code>;}
  return String(v);
}

/**
 * NewConnectionWizard — guided 4-step flow that turns a connector spec
 * into a live, scheduled connection.
 *
 * Two host modes:
 *   - standalone: reads ?connector=&version= from the URL and uses
 *     navigate() for back/done buttons (legacy /pipeline/connections/new).
 *   - embedded:   parent passes connectorName/connectorVersion via props
 *     and provides onDone(connId) / onCancel callbacks.
 *
 * Flow:
 *   Step 0  Pick (or confirm) the source         (read-only summary)
 *   Step 1  Fill in connector config             (JSON-Schema form)
 *   Step 2  Choose streams + preview rows        (uses temp conn_id)
 *   Step 3  Set sync cadence                     (manual / hourly / daily / weekly / cron)
 *
 * After step 1 we POST /connections to obtain a real conn_id we can use
 * for preview calls. If the user backs out before finishing we DELETE
 * the half-configured connection. The final "Finish" performs a PUT to
 * persist selected_streams + schedule.
 */
const SCHEDULE_PRESETS = [
  { value: '',           label: 'Manual',          icon: <ThunderboltOutlined />, hint: 'Run only when triggered.' },
  { value: '0 * * * *',  label: 'Hourly',          icon: <ClockCircleOutlined />, hint: 'Top of every hour.' },
  { value: '0 2 * * *',  label: 'Daily at 02:00',  icon: <CalendarOutlined />,    hint: 'Once per day, UTC.' },
  { value: '0 2 * * 1',  label: 'Weekly (Mon)',    icon: <CalendarOutlined />,    hint: 'Every Monday 02:00 UTC.' },
];

// ── Pure helpers extracted from NewConnectionWizard ───────────────────
// Each collapses a handful of ternaries/&&'s that used to live directly
// in the component body, or a per-step `if` block inside `handleNext`,
// into a small, independently testable function. No behavior change.

export function resolveConnectorIdentity({ embedded, connectorNameProp, connectorVersionProp, params }) {
  return {
    connectorName: embedded ? connectorNameProp : params.get('connector'),
    connectorVersion: embedded ? connectorVersionProp : params.get('version'),
  };
}

export function resolveWizardContainerStyle(embedded) {
  return {
    padding: embedded ? 0 : 24,
    maxWidth: embedded ? '100%' : WIZARD_MAX_WIDTH,
    margin: '0 auto',
  };
}

export function isBackDisabled({ step, submitting }) {
  return step === 0 || submitting;
}

export function isConnectionNameValid(name) {
  return !!name.trim();
}

export function hasNoStreamsSelected(selectedStreams) {
  return selectedStreams.length === 0;
}

export function isCustomCronMissing({ scheduleChoice, customCron }) {
  return scheduleChoice === 'custom' && !customCron.trim();
}

export function buildInitialConnectionPayload({ name, spec, values, loadMode }) {
  return {
    name: name.trim(),
    connector_name: spec.name,
    connector_version: spec.version,
    config: values,
    selected_streams: (spec.streams || []).map((s) => s.name),
    load_mode: loadMode,
  };
}

export default function NewConnectionWizard({
  embedded = false,
  connectorName: connectorNameProp,
  connectorVersion: connectorVersionProp,
  onDone,
  onCancel,
} = {}) {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { connectorName, connectorVersion } = resolveConnectorIdentity({
    embedded, connectorNameProp, connectorVersionProp, params,
  });

  // Centralised "leave wizard" handler — calls back to host in embedded
  // mode, otherwise routes back to the catalog page.
  const exit = useCallback((connIdIfDone) => {
    if (embedded) {
      if (connIdIfDone && onDone) {onDone(connIdIfDone);}
      else if (onCancel) {onCancel();}
      else { /* no callback provided — nothing to notify */ }
      return;
    }
    navigate('/pipeline/catalog');
  }, [embedded, navigate, onCancel, onDone]);

  const [spec, setSpec] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [connId, setConnId] = useState(null);     // populated after step 1 submit
  const [name, setName] = useState('');
  const [config, setConfig] = useState({});
  const [selectedStreams, setSelectedStreams] = useState([]);
  const [loadMode, setLoadMode] = useState('incremental');
  const [scheduleChoice, setScheduleChoice] = useState('0 2 * * *');
  const [customCron, setCustomCron] = useState('');
  const [previews, setPreviews] = useState({});   // { [streamName]: { loading, data } }
  const [done, setDone] = useState(false);

  // Refs read by the unmount cleanup. Using state in the dep array would
  // cause the previous cleanup to fire when `done` flips true at the end
  // of the wizard — and delete the connection the user just created.
  const connIdRef = useRef(null);
  const doneRef = useRef(false);
  useEffect(() => { connIdRef.current = connId; }, [connId]);
  useEffect(() => { doneRef.current = done; }, [done]);

  const [configForm] = Form.useForm();

  useEffect(() => {
    if (!connectorName || !connectorVersion) {
      message.error('Missing connector info — pick a source from the catalog first.');
      exit();
      return;
    }
    (async () => {
      try {
        const s = await getConnectorSpec(connectorName, connectorVersion);
        setSpec(s);
        setName(`${s.display_name || s.name} - ${new Date().toLocaleDateString()}`);
        setSelectedStreams((s.streams || []).map((st) => st.name));
      } catch (err) {
        message.error(`Couldn't load connector spec: ${err.message}`);
        exit();
      } finally {
        setLoading(false);
      }
    })();
  }, [connectorName, connectorVersion, exit]);

  // Cleanup half-configured connection if user navigates away mid-flow.
  // Mount-only effect: reads refs at unmount time so finishing the wizard
  // (which sets `done=true`) does NOT trigger a stale-closure delete.
  useEffect(() => {
    return () => {
      if (connIdRef.current && !doneRef.current) {
        deleteConnection(connIdRef.current).catch(() => {});
      }
    };
  }, []);

  const cron = useMemo(
    () => (scheduleChoice === 'custom' ? customCron.trim() : scheduleChoice),
    [scheduleChoice, customCron],
  );

  // ── Step transitions ──────────────────────────────────────────
  // Split per-step so each handler's own branching/nesting is independent
  // of the others (a single dispatcher with 4 sequential `if` blocks was
  // flagged for both cyclomatic and cognitive complexity — nesting inside
  // each block compounds across the whole function; a fresh function per
  // step resets that).
  const handleNextFromName = () => {
    if (!isConnectionNameValid(name)) {
      message.warning('Give the connection a name first.');
      return;
    }
    setStep(1);
  };

  const handleNextFromConfigure = async () => {
    // Validate + create connection so we have an id for preview.
    let values;
    try {
      values = await configForm.validateFields();
    } catch {
      return; // validation already showed errors
    }
    setSubmitting(true);
    try {
      if (!connId) {
        const created = await createConnection(buildInitialConnectionPayload({ name, spec, values, loadMode }));
        setConnId(created.id);
      } else {
        await updateConnection(connId, { name: name.trim(), config: values });
      }
      setConfig(values);
      setStep(2);
    } catch (err) {
      message.error(`Couldn't save connection: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNextFromStreams = () => {
    if (hasNoStreamsSelected(selectedStreams)) {
      message.warning('Select at least one stream to sync.');
      return;
    }
    setStep(WIZARD_STEP_SCHEDULE);
  };

  const handleNextFromSchedule = async () => {
    if (isCustomCronMissing({ scheduleChoice, customCron })) {
      message.warning('Enter a cron expression or pick a preset.');
      return;
    }
    // Final save
    setSubmitting(true);
    try {
      await updateConnection(connId, {
        selected_streams: selectedStreams,
        load_mode: loadMode,
        schedule: cron,
      });
      // Synchronously mark "done" so the unmount cleanup (which fires
      // when the host closes our drawer in response to onDone) reads
      // the latest value via the ref instead of a stale render.
      doneRef.current = true;
      setDone(true);
      // In embedded mode, hand the new id back to the host immediately
      // (avoids the unmount-cleanup deleting the connection we just made).
      if (embedded && onDone) {onDone(connId);}
    } catch (err) {
      message.error(`Couldn't finalise: ${err.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = () => {
    const stepHandlers = [
      handleNextFromName, handleNextFromConfigure, handleNextFromStreams, handleNextFromSchedule,
    ];
    stepHandlers[step]();
  };

  const handleBack = () => setStep((s) => Math.max(0, s - 1));

  const runPreview = async (streamName) => {
    setPreviews((p) => ({ ...p, [streamName]: { loading: true } }));
    try {
      // Test Connection returns {<stream>: {healthy, total_records,
      // sample_rows, sample_columns, error, sample_error, ...}} — the
      // wizard only renders the sample. Surface either a hard error
      // (connectivity/auth) or sample_error (extract failed but counts ok).
      const result = await testConnection(connId, [streamName]);
      const entry = result?.[streamName] || {};
      const data = {
        rows: entry.sample_rows || [],
        columns: entry.sample_columns || [],
        error: entry.error || entry.sample_error || null,
      };
      setPreviews((p) => ({ ...p, [streamName]: { loading: false, data } }));
    } catch (err) {
      setPreviews((p) => ({
        ...p, [streamName]: { loading: false, data: { error: err.message, rows: [] } },
      }));
    }
  };

  if (loading) {
    return <div style={{ padding: 64, textAlign: 'center' }}><Spin size="large" /></div>;
  }
  if (!spec) {return null;}

  // ── Success screen ───────────────────────────────────────────
  if (done) {
    return <WizardDoneScreen embedded={embedded} name={name} navigate={navigate} />;
  }

  return (
    <div style={resolveWizardContainerStyle(embedded)}>
      <WizardBackToCatalog embedded={embedded} onExit={exit} />

      <Card style={{ marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Connect {spec.display_name || spec.name}
        </Title>
        <Text type="secondary">{spec.description || `v${spec.version}`}</Text>
      </Card>

      <Card>
        <Steps
          current={step}
          style={{ marginBottom: 24 }}
          items={[
            { title: 'Name' },
            { title: 'Configure' },
            { title: 'Streams' },
            { title: 'Schedule' },
          ]}
        />

        {renderWizardStep(step, {
          name, setName, spec, form: configForm, config,
          selectedStreams, setSelectedStreams, loadMode, setLoadMode, previews, runPreview,
          scheduleChoice, setScheduleChoice, customCron, setCustomCron,
        })}

        <Divider />
        <Space style={{ justifyContent: 'space-between', width: '100%', display: 'flex' }}>
          <Button onClick={handleBack} disabled={isBackDisabled({ step, submitting })} icon={<ArrowLeftOutlined />}>
            Back
          </Button>
          <WizardNextButton isFinalStep={step >= 3} onClick={handleNext} loading={submitting} />
        </Space>
      </Card>
    </div>
  );
}

// Isolates the loading/success/wizard branching that used to live
// directly in NewConnectionWizard's body — a nested `if (embedded)`
// inside `if (done)` — in its own small function scope.
function WizardDoneScreen({ embedded, name, navigate }) {
  if (embedded) {
    // Host shows its own confirmation; just call onDone once and render
    // a tiny placeholder while it unmounts us.
    return <div style={{ padding: 32, textAlign: 'center' }}><Spin /></div>;
  }
  return (
    <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
      <Result
        icon={<CheckCircleFilled style={{ color: '#52c41a' }} />}
        title="Connection ready"
        subTitle={`${name} will start syncing on the schedule you picked.`}
        extra={[
          <Button key="sources" type="primary" onClick={() => navigate('/pipeline/sources')}>
            View connection
          </Button>,
          <Button key="more" onClick={() => navigate('/pipeline/sources?add=1')}>
            Add another source
          </Button>,
        ]}
      />
    </div>
  );
}

function WizardBackToCatalog({ embedded, onExit }) {
  if (embedded) {return null;}
  return (
    <Space style={{ marginBottom: 16 }}>
      <Button icon={<ArrowLeftOutlined />} onClick={() => onExit()}>
        Catalog
      </Button>
    </Space>
  );
}

function WizardNextButton({ isFinalStep, onClick, loading }) {
  return (
    <Button
      type="primary"
      onClick={onClick}
      loading={loading}
      icon={isFinalStep ? <CheckCircleFilled /> : <ArrowRightOutlined />}
    >
      {isFinalStep ? 'Finish' : 'Continue'}
    </Button>
  );
}

// Dispatches to the right step component — a lookup instead of four
// sequential `step === N &&` checks in the component body.
function renderWizardStep(step, props) {
  switch (step) {
    case 0:
      return <StepName name={props.name} setName={props.setName} spec={props.spec} />;
    case 1:
      return <StepConfigure spec={props.spec} form={props.form} initialValues={props.config} />;
    case 2:
      return (
        <StepStreams
          spec={props.spec}
          selected={props.selectedStreams}
          setSelected={props.setSelectedStreams}
          loadMode={props.loadMode}
          setLoadMode={props.setLoadMode}
          previews={props.previews}
          runPreview={props.runPreview}
        />
      );
    case WIZARD_STEP_SCHEDULE:
      return (
        <StepSchedule
          scheduleChoice={props.scheduleChoice}
          setScheduleChoice={props.setScheduleChoice}
          customCron={props.customCron}
          setCustomCron={props.setCustomCron}
        />
      );
    default:
      return null;
  }
}

// ── Step 0 ───────────────────────────────────────────────────────
function StepName({ name, setName, spec }) {
  return (
    <div>
      <Title level={5}>Name this connection</Title>
      <Paragraph type="secondary">
        Pick something memorable — e.g., "Production Jira" or "Acme Datadog (staging)".
        You can change this later.
      </Paragraph>
      <Input
        size="large"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder={spec.display_name || spec.name}
        maxLength={120}
        autoFocus
      />
    </div>
  );
}

// ── Step 1 ───────────────────────────────────────────────────────
function StepConfigure({ spec, form, initialValues }) {
  const schema = spec.config_schema || {};
  const propCount = Object.keys(schema.properties || {}).length;
  return (
    <div>
      <Title level={5}>Connection settings</Title>
      <Paragraph type="secondary">
        These come straight from the connector. Hover the help icons for what each
        field expects.
      </Paragraph>
      {propCount === 0 ? (
        <Alert
          type="info"
          showIcon
          title="This connector doesn't require any configuration."
        />
      ) : (
        <JsonSchemaForm
          schema={schema}
          form={form}
          initialValues={initialValues}
        />
      )}
    </div>
  );
}

// ── Step 2 ───────────────────────────────────────────────────────
function StepStreams({ spec, selected, setSelected, loadMode, setLoadMode, previews, runPreview }) {
  const streams = spec.streams || [];
  const allSelected = selected.length === streams.length && streams.length > 0;

  const toggle = (name, on) => {
    setSelected((prev) => on ? [...new Set([...prev, name])] : prev.filter((n) => n !== name));
  };
  const toggleAll = (on) => {
    setSelected(on ? streams.map((s) => s.name) : []);
  };

  return (
    <div>
      <Title level={5}>Pick the data you want</Title>
      <Paragraph type="secondary">
        Each stream lands as its own table in your vault. Toggle off anything you don't
        need to keep things tidy. Use <b>Preview</b> to fetch 5 rows and confirm the
        shape before scheduling.
      </Paragraph>

      <Space style={{ marginBottom: 12 }} size="middle" wrap>
        <Checkbox
          checked={allSelected}
          indeterminate={!allSelected && selected.length > 0}
          onChange={(e) => toggleAll(e.target.checked)}
        >
          Select all ({streams.length})
        </Checkbox>
        <Tag color="blue">{selected.length} selected</Tag>
        <Divider orientation="vertical" />
        <Text type="secondary">Load mode:</Text>
        <Radio.Group value={loadMode} onChange={(e) => setLoadMode(e.target.value)}>
          <Tooltip title="Only fetch records changed since the last sync (uses each stream's cursor field).">
            <Radio.Button value="incremental">Incremental</Radio.Button>
          </Tooltip>
          <Tooltip title="Re-fetch every record on every sync. Slower but always self-healing.">
            <Radio.Button value="full">Full refresh</Radio.Button>
          </Tooltip>
        </Radio.Group>
      </Space>

      {streams.length === 0 ? (
        <Empty description="This connector doesn't declare any streams." />
      ) : (
        <Space orientation="vertical" size={12} style={{ width: '100%' }}>
          {streams.map((s) => (
            <StreamPickCard
              key={s.name}
              stream={s}
              on={selected.includes(s.name)}
              preview={previews[s.name]}
              onToggle={toggle}
              onPreview={runPreview}
            />
          ))}
        </Space>
      )}
    </div>
  );
}

// One stream's pick/preview card — isolated so its own error/empty/table
// branching has its own function scope instead of living inside
// StepStreams' `.map` body (independently flagged for cyclomatic
// complexity there).
function StreamPickCard({ stream: s, on, preview, onToggle, onPreview }) {
  return (
    <Card size="small" style={{ borderColor: on ? '#1677ff' : undefined }}>
      <Space style={{ justifyContent: 'space-between', width: '100%', display: 'flex' }} align="start">
        <div>
          <Checkbox checked={on} onChange={(e) => onToggle(s.name, e.target.checked)}>
            <b>{s.name}</b>
          </Checkbox>
          <StreamCardMeta primaryKey={s.primary_key} cursorField={s.cursor_field} />
        </div>
        <Button
          size="small"
          icon={<ReloadOutlined />}
          onClick={() => onPreview(s.name)}
          loading={preview?.loading}
        >
          Test &amp; preview
        </Button>
      </Space>
      <StreamPreviewResult preview={preview} />
    </Card>
  );
}

function StreamCardMeta({ primaryKey, cursorField }) {
  const pkList = primaryKey || [];
  const hasPk = pkList.length > 0;
  return (
    <div style={{ marginLeft: 24, fontSize: 12, color: '#8c8c8c' }}>
      {hasPk && (
        <>PK: <code>{pkList.join(', ')}</code></>
      )}
      {cursorField && (
        <>{hasPk ? ' · ' : ''}Cursor: <code>{cursorField}</code></>
      )}
    </div>
  );
}

function StreamPreviewResult({ preview }) {
  if (preview?.data?.error) {
    return (
      <Alert
        type="error"
        showIcon
        style={{ marginTop: 12 }}
        title="Preview failed"
        description={preview.data.error}
      />
    );
  }
  if (!preview?.data) {return null;}
  const rows = preview.data.rows || [];
  return (
    <div style={{ marginTop: 12, overflowX: 'auto' }}>
      {rows.length === 0 ? (
        <Alert
          type="info"
          showIcon
          title="Stream returned no rows yet"
          description="That's normal for empty resources or future-dated cursors. The connector still works."
        />
      ) : (
        <Table
          size="small"
          pagination={false}
          dataSource={rows.map((r, i) => ({ ...r, _key: i }))}
          rowKey="_key"
          scroll={{ x: 'max-content' }}
          columns={(preview.data.columns || []).map((c) => ({
            title: c,
            dataIndex: c,
            ellipsis: true,
            render: renderPreviewCell,
          }))}
        />
      )}
    </div>
  );
}

// ── Step 3 ───────────────────────────────────────────────────────
function StepSchedule({ scheduleChoice, setScheduleChoice, customCron, setCustomCron }) {
  return (
    <div>
      <Title level={5}>How often should we sync?</Title>
      <Paragraph type="secondary">
        Pick a cadence — you can always trigger a sync manually too. Times are UTC.
      </Paragraph>

      <Radio.Group
        value={scheduleChoice}
        onChange={(e) => setScheduleChoice(e.target.value)}
        style={{ width: '100%' }}
      >
        <Space orientation="vertical" style={{ width: '100%' }}>
          {SCHEDULE_PRESETS.map((p) => (
            <Radio key={p.value || 'manual'} value={p.value} style={{ display: 'block', padding: '8px 0' }}>
              <Space>
                {p.icon}
                <b>{p.label}</b>
                <Text type="secondary">{p.hint}</Text>
                {p.value && <Tag style={{ fontFamily: 'monospace' }}>{p.value}</Tag>}
              </Space>
            </Radio>
          ))}
          <Radio value="custom" style={{ display: 'block', padding: '8px 0' }}>
            <Space orientation="vertical" style={{ width: '100%' }}>
              <Space><CalendarOutlined /><b>Custom cron</b></Space>
              <Input
                placeholder="e.g.  */15 * * * *"
                value={customCron}
                onChange={(e) => setCustomCron(e.target.value)}
                disabled={scheduleChoice !== 'custom'}
                style={{ maxWidth: 360 }}
              />
              <Text type="secondary" style={{ fontSize: 12 }}>
                Standard 5-field cron: <code>min hour dom mon dow</code>
              </Text>
            </Space>
          </Radio>
        </Space>
      </Radio.Group>
    </div>
  );
}
